import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Zap, 
  Truck, 
  Shield, 
  ArrowRight,
  Check,
  Star,
  Menu,
  ChevronDown,
  X,
  CheckCircle2,
  Clock,
  Mail,
  Award,
  Users,
  BadgeCheck,
  PackageCheck,
  Wrench,
  ThumbsUp,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { trackFunnelStep, FUNNEL_STEPS } from '../../utils/analytics';
import { cn } from '@/lib/utils';
import Cart from '../Cart/Cart';
import FloatingSupport from '../common/FloatingSupport';
import Footer from '../common/Footer';
import CarModelsDropdown from '../common/CarModelsDropdown';

const LandingModern = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    trackFunnelStep(FUNNEL_STEPS.LANDING_VIEW);
  }, []);

  const handleCustomizeClick = () => {
    trackFunnelStep(FUNNEL_STEPS.CUSTOMIZE_START);
    navigate('/customize');
  };

  const features = [
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Intuitiv Designer",
      description: "Skapa professionella bildekaler med vår användarvänliga online-designer. Se resultatet i realtid."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Snabb Leverans",
      description: "Från design till leverans på rekordtid. UV-beständiga material för långvarig hållbarhet."
    },
    {
      icon: <Truck className="h-6 w-6" />,
      title: "Professionell Montering",
      description: "Välj mellan DIY-installation eller professionell montering av våra certifierade installatörer."
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "5 Års Garanti",
      description: "Vi garanterar kvaliteten med 5 års garanti mot blekning och skador."
    }
  ];

  const trustFactors = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: "5 Års Garanti",
      description: "Vi står bakom våra produkter med branschledande garanti mot blekning och materialdefekter."
    },
    {
      icon: <Check className="h-6 w-6" />,
      title: "Premium Material",
      description: "Endast högkvalitativa, UV-beständiga material från erkända europeiska tillverkare."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Snabb Produktion",
      description: "Modern produktionsutrustning för snabb leverans utan att kompromissa med kvaliteten."
    },
    {
      icon: <Truck className="h-6 w-6" />,
      title: "Certifierad Montering",
      description: "Vårt nätverk av installatörer är utbildade i korrekt applicering för bästa resultat."
    }
  ];

  const faqs = [
    {
      question: "Vad händer om jag förstör dekalen när jag monterar?",
      answer: "Vi skickar en ny gratis om du följt instruktionerna. Ta bara en bild och kontakta oss inom 7 dagar. Plus: över 90% lyckas första gången med våra videoinstruktioner. Det är enklare än du tror!"
    },
    {
      question: "Kommer dekalen att skada min billack?",
      answer: "Nej. Vi använder 3M Cast vinyl med speciallim som är testat för bilanvändning. När du tar bort dekalen (även efter flera år) lossnar den rent utan att lämna märken eller skada lacken. Funkar på både matt och blank lack. Perfekt även för leasingbilar."
    },
    {
      question: "Hur länge håller dekalerna? Tål de biltvätt?",
      answer: "Minst 5 år utomhus med normal användning. Vi ger 5 års garanti. Ja, de tål både biltvätt och högtryckstvätt – bara undvik ångblåsare de första 48 timmarna efter montering. Materialet är UV-beständigt så färgerna blekar inte i solen."
    },
    {
      question: "Vad är vektorisering och varför kostar det extra?",
      answer: "Vanliga bilder (JPG, PNG) är gjorda av pixlar. När vi förstora dem till bilstorlek blir de suddiga och kantiga. Vektorisering är när vi omvandlar din bild till vektorformat – vilket betyder att den kan förstoras hur mycket som helst utan att tappa kvalitet. Resultatet blir kristallskarpt. Har du redan en vektoriserad fil (SVG, AI, PDF) är det gratis!"
    },
    {
      question: "Vad händer om jag inte är nöjd med slutresultatet?",
      answer: "Du får alltid ett digitalt korrektur innan vi trycker, så du ser exakt hur det blir. Om det ändå blir något produktionsfel, åtgärdar vi det direkt eller gör om trycket kostnadsfritt."
    },
    {
      question: "Min bilmodell finns inte i er lista, vad gör jag?",
      answer: "Kontakta oss så lägger vi till den! Ta en bild på sidan av bilen och maila till info@dekalio.nu. Vi gör en mall åt dig inom 2-3 arbetsdagar helt gratis. Vi lägger till nya bilmodeller varje vecka."
    },
    {
      question: "Hur lång är leveranstiden?",
      answer: "3-5 arbetsdagar från att du godkänt korrekturet. Vi skickar med PostNord med spårning. Behöver du det snabbare? Vi erbjuder express (1-2 dagar) för 500 kr extra. Beställ före kl 12 för samma dags produktion."
    },
    {
      question: "Kan jag lita på att designen i editorn blir exakt likadan i verkligheten?",
      answer: "Ja. Vår 3D-modell använder exakta mått från bilmodellerna. Det du ser är det du får – vi har kalibrerat färgerna mot våra tryckare. Plus: du får alltid ett digitalt korrektur att godkänna innan vi trycker."
    },
    {
      question: "Hur når jag er om jag har problem eller frågor?",
      answer: "Email: info@dekalio.nu (svarar inom 2 timmar på vardagar). Kontakta oss via chatten nere till höger för snabbast svar. Vi har svensktalande support som faktiskt kan svara på tekniska frågor. Inga automatiska svar."
    },
    {
      question: "Kan jag ha flera dekaler på samma bil?",
      answer: "Ja. Du designar hela bilen och kan placera flera dekaler på olika ytor."
    },
    {
      question: "Kan jag designa flera bilar?",
      answer: "Ja. Du kan skapa flera bilprojekt och anpassa varje bil separat."
    },
    {
      question: "Vad händer om paketet skadas under transport?",
      answer: "Alla paket är försäkrade. Ta en bild på skadan och kontakta oss direkt så skickar vi ett nytt paket samma dag. Du betalar ingenting extra. Vi hanterar allt med PostNord."
    }
  ];

  const partners = [
    "Volvo", "Tesla", "Volkswagen", "Ford", "Kia", "Mercedes", "BMW", "Toyota"
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center gap-6">
          {/* Logo/Home */}
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/images/logo.svg" alt="Dekalio" className="h-6 w-6" />
            <span className="font-semibold">Dekalio</span>
          </a>
          
          {/* Main Navigation */}
          <nav className="hidden md:flex items-center justify-center gap-1 flex-1">
            <CarModelsDropdown />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/faq')}
              className="text-sm font-medium"
            >
              FAQ
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/support')}
              className="text-sm font-medium"
            >
              Support
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/om-oss')}
              className="text-sm font-medium"
            >
              Om oss
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/kontakt')}
              className="text-sm font-medium"
            >
              Kontakt
            </Button>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 ml-auto">
            <Cart />
            <Button size="sm" variant="default" onClick={handleCustomizeClick}>
              Börja designa
            </Button>
          </div>

          {/* Mobile Menu */}
          <Button variant="ghost" size="icon" className="md:hidden ml-auto">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </nav>

      <main>
      {/* Hero Section */}
      <section className="w-full py-16 md:py-24 bg-secondary/50">
        <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <h1 className="text-5xl font-bold tracking-tighter md:text-6xl lg:text-6xl">
                Designa din bil med dekaler – direkt på rätt modell
              </h1>
              
              <p className="text-xl text-muted-foreground">
                Placera flera dekaler på bilen, se priset i realtid och designa med exakta mått och material.
              </p>
            </div>

            {/* Feature Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold mb-1 text-base">Designa direkt på din bil</h2>
                  <p className="text-sm text-muted-foreground">
                    Exakta mallar för din bilmodell, med korrekta proportioner.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                  <Zap className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold mb-1 text-base">Placera flera dekaler</h2>
                  <p className="text-sm text-muted-foreground">
                    Sidor, bak, motorhuv – bygg en helhetsdesign.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold mb-1 text-base">Realistisk förhandsvisning</h2>
                  <p className="text-sm text-muted-foreground">
                    Se hur bilen ser ut från alla vinklar innan du beställer.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                  <BadgeCheck className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold mb-1 text-base">Pris i realtid</h2>
                  <p className="text-sm text-muted-foreground">
                    Priset uppdateras när du lägger till, flyttar eller ändrar dekaler.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-0">
              <Button size="lg" onClick={handleCustomizeClick} className="px-4">
                Börja designa gratis
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative lg:ml-auto">
            <div className="rounded-xl border bg-card shadow-2xl overflow-hidden">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/header-640w.webp 640w, /images/header-1024w.webp 1024w, /images/header-1920w.webp 1920w"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 640px, 1024px"
                />
                <source
                  type="image/png"
                  srcSet="/images/header-640w.png 640w, /images/header-1024w.png 1024w, /images/header-1920w.png 1920w"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 640px, 1024px"
                />
                <img
                  src="/images/header-1024w.png"
                  alt="Dekalio Designer Interface"
                  className="w-full h-auto"
                  width="1024"
                  height="662"
                  fetchpriority="high"
                  style={{ aspectRatio: '1024 / 662' }}
                />
              </picture>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Reversal Banner */}
      <section className="bg-green-50 border-y border-green-200">
        <div className="container py-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-center md:text-left">
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-900">5 års kvalitetsgaranti</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-900">Gratis omtryck vid monteringsfel</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-900">Skadar inte bilens lack</span>
            </div>
          </div>
        </div>
      </section>



      {/* Price Transparency Section */}
      <section className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4">Transparent prissättning</Badge>
            <h2 className="text-3xl font-semibold mb-4">Du ser vad hela bilen kostar medan du designar</h2>
            <p className="text-xl text-muted-foreground">
              Priset baseras på valda ytor och storlek på dekalerna och uppdateras direkt i editorn.
            </p>
          </div>

          <Tabs defaultValue="pricing" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="pricing">Så räknas priset</TabsTrigger>
              <TabsTrigger value="includes">Vad ingår</TabsTrigger>
            </TabsList>
            
            <TabsContent value="pricing" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Prissättning baserad på storlek</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <div>
                      <p className="font-medium">Baspris (inkl. produktion)</p>
                      <p className="text-sm text-muted-foreground">Täcker materialkostnad och grundläggande design</p>
                    </div>
                    <p className="text-xl font-semibold">1 000 kr</p>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <div>
                      <p className="font-medium">Kostnad per dekalyta</p>
                      <p className="text-sm text-muted-foreground">100 kr per 10 000 pixlar (ca 25x40 cm)</p>
                    </div>
                    <p className="text-xl font-semibold">Varierar</p>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <div>
                      <p className="font-medium">Vektorisering av bilder</p>
                      <p className="text-sm text-muted-foreground">Omvandlar din bild till perfekt tryckkvalitet</p>
                    </div>
                    <p className="text-xl font-semibold">1 500 kr/bild</p>
                  </div>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Varför vektorisering?</AlertTitle>
                    <AlertDescription>
                      Vanliga bilder (JPG/PNG) blir pixliga vid stora storlekar. Vi omvandlar dem till vektorformat 
                      så de blir kristallklara oavsett storlek. Du slipper ta betalt om din bild redan är i vektorformat (SVG/AI).
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="includes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Ingår alltid i priset</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Premium 3M vinyl</p>
                        <p className="text-sm text-muted-foreground">Tåler vinter, sommar och biltvätt</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">UV-skydd</p>
                        <p className="text-sm text-muted-foreground">Blekar inte i solen</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Monteringssats</p>
                        <p className="text-sm text-muted-foreground">Verktyg + rakblad + instruktioner</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Digital korrektur</p>
                        <p className="text-sm text-muted-foreground">Se exakt hur det blir innan tryck</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Snabb leverans</p>
                        <p className="text-sm text-muted-foreground">3-5 arbetsdagar med PostNord</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Kundsupport</p>
                        <p className="text-sm text-muted-foreground">Vi hjälper dig vid problem</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Design Review Section */}
      <section className="container py-16 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <Badge className="mb-4">Valfri tjänst</Badge>
          <h2 className="text-3xl font-semibold mb-4">Vill du få din bil design granskad?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Vi går igenom helheten – placering, balans och läsbarhet – innan produktion.
          </p>
          
          <Card className="text-left max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <ul className="space-y-3 mb-6">
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Expert går igenom hela bildesignen</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Kontroll av placering och proportioner</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Feedback på läsbarhet och synlighet</span>
                </li>
              </ul>
              
              <Button className="w-full" size="lg">
                Lägg till granskning (valfritt)
              </Button>
              
              <p className="text-sm text-muted-foreground text-center mt-4">
                Rekommenderas för första firmabilen
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Material Quality Section */}
      <section className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4">Material</Badge>
            <h2 className="text-3xl font-semibold mb-4">Material anpassat för firmabilar</h2>
            <p className="text-xl text-muted-foreground">
              Samma material som används för professionell fordonsprofilering
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <PackageCheck className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Skonsam mot billack</h3>
                <p className="text-muted-foreground">
                  Tas bort utan spår. Fungerar på både matt och blank lack.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Tål tvätt, väder och daglig körning</h3>
                <p className="text-muted-foreground">
                  Från -40°C till +80°C. Tåler biltvätt och högtryckstvätt.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <BadgeCheck className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Samma material som professionella använder</h3>
                <p className="text-muted-foreground">
                  3M IJ180Cv3 – används på polisbilar och ambulanser.
                </p>
              </CardContent>
            </Card>
            </div>

          <Alert>
            <BadgeCheck className="h-5 w-5" />
            <AlertTitle>5 års garanti ingår</AlertTitle>
            <AlertDescription className="text-base">
              Om dekalen lossnar, blekar eller på annat sätt inte håller måttet inom 5 år, 
              skickar vi en ny helt kostnadsfritt. Inga krångliga villkor.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      <Separator />

      {/* Features Grid */}
      <section className="container py-24 md:py-32">
        <div className="mx-auto flex max-w-[980px] flex-col items-center gap-4 text-center mb-16">
          <Badge variant="outline">Funktioner</Badge>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            Skräddarsydda verktyg för moderna företag
          </h2>
          <p className="max-w-[750px] text-lg text-muted-foreground">
            Dekalio är byggt på de vanor som gör de bästa företagen framgångsrika: 
            hålla fokus, röra sig snabbt och alltid sikta på högkvalitativt arbete.
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, i) => (
            <Card key={i} className="relative overflow-hidden">
              <CardHeader>
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Features with Images */}
        <div className="mt-24 space-y-24">
          <div className="flex justify-center">
            <div className="max-w-3xl text-center">
              <Badge variant="outline" className="mb-4">Designer</Badge>
              <h2 className="text-3xl font-bold mb-4">
                Kraftfull online-designer
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Släpp loss din kreativitet med vår användarvänliga designer. 
                Ladda upp bilder, lägg till text och experimentera med färger. 
                Se dina ändringar i realtid på din faktiska bilmodell.
              </p>
              <ul className="space-y-3 text-left inline-block">
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Drag-and-drop gränssnitt</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Realtidsförhandsgranskning</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Exakta bilmallar</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="max-w-3xl text-center">
              <Badge variant="outline" className="mb-4">Precision</Badge>
              <h2 className="text-3xl font-bold mb-4">
                Exakta mallar för din bil
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                I vår editor hittar du exakta mallar för de flesta bilmodeller. 
                Känn dig trygg med att dina dekaler kommer passa perfekt och ge 
                en enhetlig, professionell look.
              </p>
              <Button onClick={handleCustomizeClick}>
                Utforska mallar
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* Trust Factors */}
      <section className="container py-24 md:py-32">
        <div className="mx-auto flex max-w-[980px] flex-col items-center gap-4 text-center mb-16">
          <Badge variant="outline">Kvalitet & Pålitlighet</Badge>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            Professionell standard i varje dekal
          </h2>
          <p className="max-w-[750px] text-lg text-muted-foreground">
            Vi använder branschens bästa material och metoder för att säkerställa långvarig kvalitet och resultat du kan lita på.
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-4">
          {trustFactors.map((factor, i) => (
            <Card key={i} className="text-center">
              <CardHeader>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">
                  {factor.icon}
                </div>
                <CardTitle className="text-lg">{factor.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  {factor.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quality Promise */}
        <div className="mx-auto mt-16 max-w-3xl">
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Vår kvalitetsgaranti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">100%</div>
                  <p className="text-sm text-muted-foreground">Nöjd-kund-garanti</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">5-7</div>
                  <p className="text-sm text-muted-foreground">Dagars leverans</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">5+</div>
                  <p className="text-sm text-muted-foreground">Års hållbarhet</p>
                </div>
              </div>
              <Separator />
              <p className="text-center text-sm text-muted-foreground">
                Om du inte är 100% nöjd med kvaliteten åtgärdar vi problemet eller återbetalar din beställning.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator />

      {/* FAQ */}
      <section id="faq" className="container py-24 md:py-32">
        <div className="mx-auto flex max-w-[980px] flex-col items-center gap-4 text-center mb-16">
          <div className="mx-auto mb-4">
            <HelpCircle className="h-12 w-12 text-primary mx-auto mb-4" />
          </div>
          <Badge variant="outline">FAQ</Badge>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            Vanliga frågor
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl">
            Vi svarar på det mesta här. Hittar du inte svar? Kontakta oss via email.
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Fick du inte svar på din fråga?</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild>
                <a href="mailto:info@dekalio.nu" className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Skicka email
                </a>
              </Button>
            </div>
                </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-y bg-gradient-to-br from-primary/10 to-primary/5">
        <div className="container py-24 md:py-32">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
            <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl">
              Redo att designa din firmabil?
            </h2>
            <p className="max-w-3xl text-xl text-muted-foreground">
              Se exakt hur den blir, med rätt mått, rätt material och pris i realtid.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6 w-full max-w-2xl">
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background/50">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <p className="font-semibold">5 års garanti</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background/50">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <p className="font-semibold">Gratis omtryck vid fel</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background/50">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <p className="font-semibold">Skadar inte lacken</p>
        </div>
            </div>

            <Button size="lg" className="text-lg px-10 py-6" onClick={handleCustomizeClick}>
              Börja designa din bil
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <p className="text-sm text-muted-foreground">
              ⚡ Designa gratis • Betala först när du är nöjd
            </p>
          </div>
        </div>
      </section>
      </main>

      <Footer />

      {/* Floating Support Widget */}
      <FloatingSupport />
    </div>
  );
};

export default LandingModern;

